import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Button, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';

const AdminContentApproval = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/content-approval');
      setItems(data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/content-approval/${id}/approve`);
      loadItems();
    } catch (err) { console.error(err); }
  };

  const handleReject = async () => {
    try {
      await api.put(`/content-approval/${selectedItem._id}/reject`, { reason: rejectReason });
      setRejectDialog(false);
      setRejectReason('');
      loadItems();
    } catch (err) { console.error(err); }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>{t('content_approval')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review and approve supplier-submitted content before it goes live.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Submitted By</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item._id} hover>
                <TableCell><Chip label={item.contentType || 'product'} size="small" /></TableCell>
                <TableCell>{item.title || item.product?.name || 'N/A'}</TableCell>
                <TableCell>{item.submittedBy?.firstName} {item.submittedBy?.lastName}</TableCell>
                <TableCell>{new Date(item.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Chip label={item.status} size="small"
                    color={item.status === 'approved' ? 'success' : item.status === 'rejected' ? 'error' : 'warning'} />
                </TableCell>
                <TableCell>
                  {item.status === 'pending' && (
                    <>
                      <Button size="small" color="success" startIcon={<CheckCircle />}
                        onClick={() => handleApprove(item._id)}>
                        Approve
                      </Button>
                      <Button size="small" color="error" startIcon={<Cancel />}
                        onClick={() => { setSelectedItem(item); setRejectDialog(true); }}>
                        Reject
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No pending content approvals
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Content</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>Provide a reason for rejection:</Typography>
          <TextField fullWidth multiline rows={3} value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminContentApproval;
